export const normalizeEmail = (email) => String(email || '').trim().toLowerCase();
const nonempty = (value) => typeof value === 'string' && value.trim().length > 0;
const positive = (value) => Number.isFinite(value) && value > 0;

export function readChallenge(data, email, previousId) {
  if (!nonempty(data?.email) || normalizeEmail(data.email) !== normalizeEmail(email) ||
      !nonempty(data?.challenge_id) || data.challenge_id === previousId ||
      !positive(data?.expires_in_minutes) || !positive(data?.retry_after_seconds)) return null;
  return {
    email: normalizeEmail(data.email),
    challenge_id: data.challenge_id,
    expires_at: Date.now() + data.expires_in_minutes * 60000,
  };
}

export function readVerification(data, challenge) {
  if (data?.verified !== true || !nonempty(data?.verification_token) ||
      !positive(data?.verification_expires_in_minutes) || !challenge) return null;
  return {
    email: challenge.email,
    challenge_id: challenge.challenge_id,
    verification_token: data.verification_token,
    verification_expires_at: Date.now() + data.verification_expires_in_minutes * 60000,
  };
}

export function hasPartnerSignupProof(data) {
  return Boolean(nonempty(data?.email) && data.email === data.verified_email &&
    nonempty(data.challenge_id) && nonempty(data.verification_token) &&
    data.verification_expires_at > Date.now() && nonempty(data.password) &&
    data.password === data.confirm_password);
}

export function retryAfterSeconds(error) {
  const headers = error?.response?.headers;
  const value = (typeof headers?.get === 'function' ? headers.get('Retry-After') : undefined) ??
    headers?.['retry-after'] ?? headers?.['Retry-After'] ?? error?.retry_after_seconds;
  const seconds = typeof value === 'string' || typeof value === 'number' ? Number(value) : NaN;
  if (Number.isFinite(seconds)) return seconds > 0 ? Math.ceil(seconds) : 60;
  const date = typeof value === 'string' ? Date.parse(value) : NaN;
  return Number.isFinite(date) ? Math.max(1, Math.ceil((date - Date.now()) / 1000)) : 60;
}

const errorMessageText = (value) => typeof value === 'string'
  ? value.trim().replace(/^Value error,\s*/i, '').trim() || undefined
  : undefined;

export function authErrorMessage(error, fallback = 'Request failed. Please try again.') {
  const status = error?.response?.status ?? error?.status;
  if (status == null && (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED' || error?.request)) {
    return 'Unable to reach the Yet2Book service. Please try again shortly.';
  }
  if (status === 429) return `Too many attempts. Please wait ${retryAfterSeconds(error)} seconds before trying again.`;
  if (status === 503) return 'Service temporarily unavailable. Please try again later.';
  const errors = error?.response?.data?.errors ?? error?.data?.errors;
  // Read only message fields, never serialize errors or their input/body/request data.
  if (status === 422 && Array.isArray(errors)) {
    const message = errors.map(entry => errorMessageText(entry?.message)).find(Boolean);
    if (message) return message;
  }
  const detail = error?.response?.data?.detail ?? error?.data?.detail;
  const message = Array.isArray(detail)
    ? detail.map(entry => errorMessageText(entry?.msg) || errorMessageText(entry?.message)).find(Boolean)
    : errorMessageText(detail);
  return message || fallback;
}